import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Search, Send, Edit2, Trash2, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// FEATURE 8: Nested Comment Component
const Comment = ({ comment, postId, onCommentAdded, onCommentUpdated, onCommentDeleted, level = 0 }) => {
  const [isReplying, setIsReplying] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [loading, setLoading] = useState(false);
  const { toast} = useToast();
  const { user } = useAuth();
  
  const canEdit = user && (user.id === comment.author_id || user.is_admin);
  
  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyContent.trim()) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/posts/${postId}/comments`, {
        content: replyContent.trim(),
        parent_id: comment.id,  // FEATURE 8: Set parent_id for nested comment
      });
      
      toast({
        title: 'Reply posted!',
        description: 'Your reply has been added.',
      });
      
      setReplyContent('');
      setIsReplying(false);
      onCommentAdded(response.data);
    } catch (error) {
      toast({
        title: 'Failed to post reply',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editContent.trim()) return;
    
    setLoading(true);
    try {
      const response = await axios.patch(`${API}/posts/${postId}/comments/${comment.id}`, {
        content: editContent.trim(),
      });
      
      toast({
        title: 'Comment updated',
        description: 'Your comment has been updated.',
      });
      
      setIsEditing(false);
      onCommentUpdated(response.data);
    } catch (error) {
      toast({
        title: 'Failed to update comment',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      await axios.delete(`${API}/posts/${postId}/comments/${comment.id}`);
      toast({
        title: 'Comment deleted',
        description: 'Your comment has been removed.',
      });
      onCommentDeleted(comment.id);
    } catch (error) {
      toast({
        title: 'Failed to delete comment',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    }
  };
  
  const marginLeft = level > 0 ? 'ml-8' : '';  // Indent nested comments
  const borderLeft = level > 0 ? 'border-l-2 border-gray-200 pl-4' : '';
  
  return (
    <div className={`${marginLeft} ${borderLeft} mb-4`}>
      <div className="bg-gray-50 rounded-lg p-4">
        {isEditing ? (
          <form onSubmit={handleEdit} className="space-y-2">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent"
              rows="3"
            />
            <div className="flex space-x-2">
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-[#FF8C00] text-white rounded-lg hover:bg-[#E67E00] transition disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setEditContent(comment.content);
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="flex items-start justify-between mb-2">
              <div>
                <span className="font-semibold text-gray-900">{comment.author_screenname}</span>
                <span className="text-sm text-gray-500 ml-2">
                  {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                </span>
                {comment.updated_at && (
                  <span className="text-xs text-gray-400 ml-2">(edited)</span>
                )}
              </div>
              {canEdit && (
                <div className="flex space-x-2">
                  <button
                    onClick={() => setIsEditing(true)}
                    className="text-gray-500 hover:text-[#FF8C00] transition"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleDelete}
                    className="text-gray-500 hover:text-red-600 transition"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            <p className="text-gray-700 whitespace-pre-wrap">{comment.content}</p>
            
            {/* Reply button */}
            {user && (
              <button
                onClick={() => setIsReplying(!isReplying)}
                className="mt-2 text-sm text-[#7B3F9E] hover:text-[#FF8C00] transition flex items-center space-x-1"
              >
                <MessageCircle className="w-3 h-3" />
                <span>Reply</span>
              </button>
            )}
          </>
        )}
      </div>
      
      {/* Reply form */}
      {isReplying && (
        <form onSubmit={handleReply} className="mt-3 ml-8">
          <div className="flex space-x-2">
            <input
              type="text"
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="Write a reply..."
              className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent"
            />
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-[#7B3F9E] text-white rounded-lg hover:bg-[#6A3587] transition disabled:opacity-50"
            >
              {loading ? 'Posting...' : 'Reply'}
            </button>
          </div>
        </form>
      )}
      
      {/* FEATURE 8: Render nested replies recursively */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="mt-3">
          {comment.replies.map((reply) => (
            <Comment
              key={reply.id}
              comment={reply}
              postId={postId}
              onCommentAdded={onCommentAdded}
              onCommentUpdated={onCommentUpdated}
              onCommentDeleted={onCommentDeleted}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const CommentSection = ({ postId, comments, onCommentAdded, inline = false }) => {
  const [newComment, setNewComment] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');  // FEATURE 7: Sort state
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { user, isAuthenticated } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newComment.trim()) {
      toast({
        title: 'Comment is empty',
        description: 'Please write something before posting',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/posts/${postId}/comments`, {
        content: newComment.trim(),
      });

      toast({
        title: 'Comment posted!',
        description: 'Your comment has been added to the conversation.',
      });

      setNewComment('');
      onCommentAdded(response.data);
    } catch (error) {
      toast({
        title: 'Failed to post comment',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredComments = comments.filter((comment) =>
    comment.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // FEATURE 7: Sort comments based on selected order
  const sortedComments = [...filteredComments].sort((a, b) => {
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
  });

  return (
    <div className="space-y-4" data-testid="comment-section">
      {/* Search and Sort - only show in non-inline mode */}
      {!inline && (
        <div className="flex space-x-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search comments..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent outline-none transition"
              data-testid="search-comment-input"
            />
          </div>
          {/* FEATURE 7: Sort dropdown */}
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent outline-none transition"
            data-testid="sort-comments-select"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>
        </div>
      )}

      {/* Add Comment */}
      {isAuthenticated ? (
        <form onSubmit={handleSubmit} className="bg-[#FFF4E6] rounded-lg p-4 border border-[#FFE0B3]">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {inline ? 'Add a comment' : 'Join the conversation'}
          </label>
          <div className="flex space-x-3">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Share your thoughts..."
              rows={inline ? 2 : 3}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent outline-none transition resize-none"
              data-testid="new-comment-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="h-fit bg-[#FF8C00] text-white px-4 py-3 rounded-lg hover:bg-[#E67E00] transition disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-comment-button"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </form>
      ) : (
        <div className="bg-[#FFF4E6] rounded-lg p-6 text-center border border-[#FFE0B3]">
          <p className="text-gray-700 mb-2">Please log in to join the conversation</p>
          <a
            href="/login"
            className="text-[#FF8C00] font-semibold hover:underline"
          >
            Sign in
          </a>
        </div>
      )}

      {/* Comments List - FEATURE 8: Nested Comments */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-900" data-testid="comments-count">
          {filteredComments.length} {filteredComments.length === 1 ? 'Comment' : 'Comments'}
        </h3>

        {filteredComments.length === 0 ? (
          <div className="text-center py-6 text-gray-500 text-sm">
            {searchQuery ? 'No comments match your search' : 'No comments yet. Be the first to comment!'}
          </div>
        ) : (
          sortedComments.map((comment) => (
            <Comment
              key={comment.id}
              comment={comment}
              postId={postId}
              onCommentAdded={onCommentAdded}
              onCommentUpdated={onCommentAdded}  // Refresh on update
              onCommentDeleted={onCommentAdded}  // Refresh on delete
              level={0}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default CommentSection;
